import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, isDevMode, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { SharedModule } from 'src/Shared/shared.module';
import { environment } from 'src/environments/environment';
import { AuthModule, AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Auth0ConfigService } from '@Core/services/auth0-config.service';
import { AuthTenantContextService } from '@Core/services/auth-tenant-context.service';
import { Auth0ProviderFactory } from '@Core/services/auth0-provider-factory.service';

import { AccountsService as CodeGenAccountService } from '@Core/services/codegen/accounts.service';
import { DataService } from '@Core/services/codegen/data.service';
import { DocumentsService } from '@Core/services/codegen/documents.service';
import { ProgramsService } from '@Core/services/codegen/programs.service';
import { TenantService } from '@Core/services/codegen/tenant.service';
import { WorkflowsService } from '@Core/services/codegen/workflows.service';

import { HttpInterceptorProviders } from '@Core/interceptors';
import { AccountsService } from '@Services/account-accounts.service';
import { AccountWorkflowService } from '@Services/account-workflow.service';
import { ApiManagementService } from '@Services/api-management.service';
import { DocumentService } from '@Services/document.services';
import { ErrorHandlerService } from '@Services/error-handler.service';
import { FeatureFacetService } from '@Services/feature-facet.service';
import { GlobalMessages } from '@Services/global-messages';
import { LoadingService } from '@Services/loading.service';
import { LoggingService } from '@Services/logging.service';
import { NotificationService } from '@Services/notification.service';
import { PollingService } from '@Services/polling.service';
import { ProgramProgramsDataStoresService } from '@Services/program-programs-datastores.service';
import { ProgramProgramsService } from '@Services/program-programs.service';
import { ProgramRevisionWorkflowService } from '@Services/program-revision-workflow.service';
import { ReportsService } from '@Services/reports.service';
import { SyncHubStatusService } from '@Services/sync-hub-status.service';
import { TenantAssignableService } from '@Services/tenant-assignable.service';
import { TenantDataService } from '@Services/tenant-data.service';
import { TenantTenantService } from '@Services/tenant-tenant.service';
import { UserAccountsService } from '@Services/user-accounts.service';
import { UserWorkflowsService } from '@Services/user-workflows.service';
import { HttpClientModule } from '@angular/common/http';
import { LoginContainerComponent } from '@Mktg/Login/login-container.component';
import { LoginComponent } from '@Mktg/Login/login.component';
import { RegisterComponent } from '@Mktg/Register/register.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AdminPollingService } from '@Admin/admin-polling.service';
import { AuthService } from '@Services/auth.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AlertsHub } from '@Core/hubs/alerts.hub';
import { ClearanceHub } from "@Core/hubs/clearance.hub";
import { SelectAreaComponent } from '@Mktg/select-area/select-area.component';
import { SelectTenantComponent } from '@Mktg/select-tenant/select-tenant.component';
import { SelectAreaModalComponent } from '@Mktg/select-area/select-area-modal.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OverlayModule } from '@angular/cdk/overlay';
import { NgxsModule } from '@ngxs/store';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { GridModule } from '@progress/kendo-angular-grid';
import 'hammerjs';
import { AppConfigService } from '@Core/services/proprietary/app-config.service';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService as BaseDataService } from '@Core/services/proprietary/data.service';
import { DatasourceService } from '@Core/services/proprietary/datasource.service';
import { MenuService } from '@Core/services/proprietary/menu.service';
import { PageParameterService } from '@Core/services/proprietary/page-parameter.service';
import { PermissionService } from '@Core/services/proprietary/permission.service';
import { TaskControlModule } from '@Work/Accounts/proprietary/controls/task-controls/task-control.module';
import { UxDesignerModule } from '@Work/Accounts/proprietary/controls/ux-designer-controls/ux-designer.module';
import { ObjectCompareDialogModule } from '@Work/Accounts/proprietary/dialogs/object-compare-dialog/object-compare-dialog.module';
import { RequiredEventDialogModule } from '@Work/Accounts/proprietary/dialogs/required-event-dialog/required-event-dialog.module';
import { SelectionDialogModule } from '@Work/Accounts/proprietary/dialogs/selection-dialog/selection-dialog.module';
import { ValidationDialogModule } from '@Work/Accounts/proprietary/dialogs/validation-dialog/validation-dialog.module';
import { DirectiveModule } from '@Shared/Directives/proprietary/directive.module';
import { DynamicContentOutletModule } from '@Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-outlet.module';
import { DocumentDetailObjectModule } from '@Core/models/proprietary/document-detail/document-detail-object.module';
import { DocumentViewerObjectModule } from '@Core/models/proprietary/document-viewer/document-viewer-object.module';
import { DocumentObjectModule } from '@Core/models/proprietary/document/document-object.module';
import { InterfaceObjectModule } from '@Core/models/proprietary/interface/interface-object.module';
import { InterfaceObjectService } from '@Core/models/proprietary/interface/interface-object.service';
import { ProjectObjectModule } from '@Core/models/proprietary/project/project-object.module';
import { TaskObjectModule } from '@Core/models/proprietary/task/task-object.module';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';
import { BookCategoryService } from '@Core/services/proprietary/book-categories.service';
import { ConfirmationDialogService } from '@Core/services/proprietary/confirmation-dialog/confirmation-dialog.service';
import { DateFormulaService } from '@Core/services/proprietary/date-formula.service';
import { DocumentDistributeService } from '@Core/services/proprietary/document-distribute.service';
import { ObjectCompareService } from '@Core/services/proprietary/object-compare.service';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { SearchService } from '@Core/services/proprietary/search.service';
import { SelectionDialogService } from '@Core/services/proprietary/selection-dialog.service';

import './aurora-elements';

// Helper function to strip https:// prefix from domain if present
function stripHttpsPrefix(domain: string): string {
    return domain?.startsWith('https://') ? domain.substring(8) : domain;
}

@NgModule({
    imports: [
        AuthModule.forRoot({
            domain: stripHttpsPrefix(environment.auth.authUri),
            clientId: environment.auth.clientId,
            authorizationParams: {
                redirect_uri: environment.baseUrl,
            },
            skipRedirectCallback: true,  // Disable automatic callback handling
            cacheLocation: 'localstorage'  // Use localStorage to persist across redirects
        }),
        SharedModule.forRoot(),

        BrowserModule,
        AppRoutingModule,
        NgbModule,
        DialogsModule,
        HttpClientModule,
        BrowserAnimationsModule,
        NgxPermissionsModule.forRoot(),

        // Modules needed for Bureau rating screens
        StoreModule.forRoot({}),
        EffectsModule.forRoot([]),
        // https://v17.ngrx.io/guide/store-devtools/config
        // turn it off: https://v17.ngrx.io/guide/store-devtools/recipes/exclude
        StoreDevtoolsModule.instrument({
            maxAge: 100, // remember the last n actions - false means unbounded (we can dispatch many actions in a single ui 'transaction' and also have recurring/polling actions)
            logOnly: !isDevMode(), // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
            trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
            traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
            connectInZone: true, // If set to true, the connection is established within the Angular zone
        }),

        // Modules needed for Proprietary rating screens
        NgxsModule.forRoot([], { developmentMode: !environment.production }),
        TaskObjectModule,
        ProjectObjectModule,
        TaskControlModule,
        DynamicContentOutletModule,
        DocumentViewerObjectModule,
        DocumentObjectModule,
        DirectiveModule,
        DocumentDetailObjectModule,
        ValidationDialogModule,
        SelectionDialogModule,
        RequiredEventDialogModule,
        ObjectCompareDialogModule,
        UxDesignerModule,
        OverlayModule,
        InterfaceObjectModule,
        GridModule,
        ChartsModule,
    ],
    declarations: [
        AppComponent,
        LoginComponent,
        LoginContainerComponent,
        RegisterComponent,
        SelectTenantComponent,
        SelectAreaComponent,
        SelectAreaModalComponent
    ],
    providers: [
        provideZoneChangeDetection(),
        HttpInterceptorProviders,
        GlobalMessages,
        LoggingService,
        NotificationService,
        ApiManagementService,
        PollingService,
        AuthService,
        Auth0ConfigService,
        AuthTenantContextService,
        Auth0ProviderFactory,

        // Code Gen services
        ProgramsService,
        TenantService,
        CodeGenAccountService,
        DataService,
        DocumentsService,
        WorkflowsService,

        AccountsService,
        ProgramProgramsService,
        TenantDataService,
        TenantTenantService,
        TenantAssignableService,
        AccountWorkflowService,
        UserWorkflowsService,
        UserAccountsService,
        ProgramProgramsDataStoresService,
        DocumentService,
        ProgramRevisionWorkflowService,
        SyncHubStatusService,
        FeatureFacetService,

        LoadingService,
        Auth0Service,
        ReportsService,
        AdminPollingService,

        AlertsHub,
        ClearanceHub,

        { provide: ErrorHandler, useClass: ErrorHandlerService },

        // Services needed for Proprietary rating screens
        PageParameterService,
        AppConfigService,
        MenuService,
        AppService,
        BaseDataService,
        DatasourceService,
        PermissionService,
        SearchService,
        ConfirmationDialogService,
        DocumentDistributeService,
        BookCategoryService,
        ObjectService,
        SelectionDialogService,
        AlertDialogService,
        ObjectCompareService,
        DateFormulaService,
        InterfaceObjectService,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent]
})
export class AppModule { }
